import { useState, useCallback, useRef } from 'react'
import type { PlayingCard, BaccaratBetType, ChipValue } from '@/types'
import {
  createDeck,
  calculateHandValue,
  isNatural,
  shouldBankerDraw,
  determineWinner,
  calculatePayout,
} from '../utils/baccaratLogic'
import { useAuth } from '@/features/auth/contexts/AuthContext'
import { delay } from '@/lib/utils'
import { recordGameTransaction } from '@/features/stats'

export type GamePhase = 'betting' | 'dealing' | 'result'
export type DealingTarget = 'player' | 'banker' | null

interface UseBaccaratGameReturn {
  // State
  playerHand: PlayingCard[]
  bankerHand: PlayingCard[]
  selectedBet: BaccaratBetType | null
  betAmount: number
  selectedChip: ChipValue
  gamePhase: GamePhase
  winner: BaccaratBetType | null
  lastWinnings: number
  isDealing: boolean
  dealingTo: DealingTarget

  // Actions
  selectBet: (betType: BaccaratBetType) => void
  selectChip: (value: ChipValue) => void
  clearBet: () => void
  deal: () => Promise<void>
  newGame: () => void
}

export function useBaccaratGame(): UseBaccaratGameReturn {
  const { user, updateBalance, isDemo } = useAuth()
  const deckRef = useRef<PlayingCard[]>([])

  const [playerHand, setPlayerHand] = useState<PlayingCard[]>([])
  const [bankerHand, setBankerHand] = useState<PlayingCard[]>([])
  const [selectedBet, setSelectedBet] = useState<BaccaratBetType | null>(null)
  const [betAmount, setBetAmount] = useState(0)
  const [selectedChip, setSelectedChip] = useState<ChipValue>(100)
  const [gamePhase, setGamePhase] = useState<GamePhase>('betting')
  const [winner, setWinner] = useState<BaccaratBetType | null>(null)
  const [lastWinnings, setLastWinnings] = useState(0)
  const [isDealing, setIsDealing] = useState(false)
  const [dealingTo, setDealingTo] = useState<DealingTarget>(null)

  // Initialize deck if empty
  const ensureDeck = useCallback(() => {
    if (deckRef.current.length < 52) {
      deckRef.current = createDeck()
    }
  }, [])

  // Draw a card from the deck
  const drawCard = useCallback((): PlayingCard => {
    ensureDeck()
    return deckRef.current.pop()!
  }, [ensureDeck])

  // Select bet type and add chips
  const selectBet = useCallback((betType: BaccaratBetType) => {
    if (gamePhase !== 'betting' || isDealing) return
    if (!user) return

    const newBet = betAmount + selectedChip
    if (newBet > user.balance) {
      return // Not enough balance
    }

    setSelectedBet(betType)
    setBetAmount(newBet)
  }, [gamePhase, isDealing, user, betAmount, selectedChip])

  // Select chip value
  const selectChip = useCallback((value: ChipValue) => {
    if (gamePhase !== 'betting') return
    setSelectedChip(value)
  }, [gamePhase])

  // Clear bet
  const clearBet = useCallback(() => {
    if (gamePhase !== 'betting') return
    setSelectedBet(null)
    setBetAmount(0)
    setSelectedChip(100)
  }, [gamePhase])

  // Deal cards
  const deal = useCallback(async () => {
    if (!selectedBet || betAmount <= 0 || isDealing || !user) return
    if (betAmount > user.balance) return

    setIsDealing(true)
    setGamePhase('dealing')

    // Deduct bet from balance
    await updateBalance(-betAmount)

    // Clear hands
    const newPlayerHand: PlayingCard[] = []
    const newBankerHand: PlayingCard[] = []

    // Deal initial cards (alternating) - animaciones más lentas
    setDealingTo('player')
    await delay(850)
    newPlayerHand.push(drawCard())
    setPlayerHand([...newPlayerHand])
    setDealingTo(null)

    await delay(400)
    setDealingTo('banker')
    await delay(850)
    newBankerHand.push(drawCard())
    setBankerHand([...newBankerHand])
    setDealingTo(null)

    await delay(400)
    setDealingTo('player')
    await delay(850)
    newPlayerHand.push(drawCard())
    setPlayerHand([...newPlayerHand])
    setDealingTo(null)

    await delay(400)
    setDealingTo('banker')
    await delay(850)
    newBankerHand.push(drawCard())
    setBankerHand([...newBankerHand])
    setDealingTo(null)

    // Check for naturals
    const playerValue = calculateHandValue(newPlayerHand)

    if (isNatural(newPlayerHand) || isNatural(newBankerHand)) {
      // Natural - no more cards
      await delay(500)
    } else {
      // Third card rules
      let playerThirdCard: PlayingCard | null = null

      // Player third card
      if (playerValue <= 5) {
        await delay(500)
        setDealingTo('player')
        await delay(850)
        playerThirdCard = drawCard()
        newPlayerHand.push(playerThirdCard)
        setPlayerHand([...newPlayerHand])
        setDealingTo(null)
      }

      // Banker third card
      const updatedBankerValue = calculateHandValue(newBankerHand)
      if (shouldBankerDraw(updatedBankerValue, playerThirdCard)) {
        await delay(500)
        setDealingTo('banker')
        await delay(850)
        newBankerHand.push(drawCard())
        setBankerHand([...newBankerHand])
        setDealingTo(null)
      }
    }

    // Determine winner
    await delay(500)
    const gameWinner = determineWinner(newPlayerHand, newBankerHand)
    setWinner(gameWinner)

    // Calculate and apply winnings
    const payoutResult = calculatePayout(selectedBet, gameWinner, betAmount)
    setLastWinnings(payoutResult.winnings)

    if (payoutResult.winnings > 0) {
      await updateBalance(payoutResult.winnings)
    }

    // Record game transaction for stats
    if (!isDemo && user) {
      const statResult = payoutResult.isWin ? 'win' : 'loss'
      recordGameTransaction(
        user.uid,
        user.email,
        user.displayName || 'Usuario',
        'baccarat',
        betAmount,
        payoutResult.winnings,
        statResult,
        { bet: selectedBet, winner: gameWinner, playerValue: calculateHandValue(newPlayerHand), bankerValue: calculateHandValue(newBankerHand) }
      )
    }

    setGamePhase('result')
    setIsDealing(false)
  }, [selectedBet, betAmount, isDealing, user, updateBalance, drawCard, isDemo])

  // Start new game
  const newGame = useCallback(() => {
    setPlayerHand([])
    setBankerHand([])
    setSelectedBet(null)
    setBetAmount(0)
    setSelectedChip(100)
    setGamePhase('betting')
    setWinner(null)
    setLastWinnings(0)
    setIsDealing(false)
  }, [])

  return {
    playerHand,
    bankerHand,
    selectedBet,
    betAmount,
    selectedChip,
    gamePhase,
    winner,
    lastWinnings,
    isDealing,
    dealingTo,
    selectBet,
    selectChip,
    clearBet,
    deal,
    newGame,
  }
}
